import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companiesAPI } from '../lib/api';
import { getCurrentCompanyId, setCurrentCompanyData } from '../utils/auth';

/**
 * Hook to get current company data
 */
export const useCurrentCompany = () => {
  const companyId = getCurrentCompanyId();
  
  return useQuery({
    queryKey: ['company', companyId],
    queryFn: async () => {
      if (!companyId) {
        console.warn('No company ID available');
        return null;
      }

      try {
        const response = await companiesAPI.getById(companyId);
        console.log('Company data response:', response.data);
        
        let companyData = null;
        if (response.data) {
          if (response.data.Success && response.data.Data) {
            companyData = response.data.Data;
          } else if (response.data.success && response.data.data) {
            companyData = response.data.data;
          } else if (response.data.id || response.data.Id) {
            companyData = response.data;
          }
        }
        
        if (companyData) {
          // Map API fields to consistent format
          const mappedCompany = {
            id: companyData.Id || companyData.id,
            name: companyData.Name || companyData.name || companyData.companyName,
            email: companyData.Email || companyData.email,
            phone: companyData.Phone || companyData.phone,
            currency: companyData.Currency || companyData.currency || 'USD',
            address: {
              street: companyData.Address || companyData.address?.street || companyData.street,
              city: companyData.City || companyData.address?.city || companyData.city,
              state: companyData.State || companyData.address?.state || companyData.state,
              zipCode: companyData.ZipCode || companyData.address?.zipCode || companyData.zipCode,
              country: companyData.Country || companyData.address?.country || companyData.country,
              email: companyData.Email || companyData.email,
              phone: companyData.Phone || companyData.phone
            },
            taxId: companyData.TaxId || companyData.taxId,
            fiscalYearEnd: companyData.FiscalYearEnd || companyData.fiscalYearEnd,
            status: companyData.Status || companyData.status,
            createdAt: companyData.CreatedAt || companyData.createdAt,
            updatedAt: companyData.UpdatedAt || companyData.updatedAt
          };
          
          // Update user data with company information
          setCurrentCompanyData(mappedCompany);
          
          return mappedCompany;
        }
        
        return null;
      } catch (error) {
        console.error('Error fetching company data:', error);
        return null;
      }
    },
    enabled: !!companyId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    cacheTime: 30 * 60 * 1000, // 30 minutes
  });
};

/**
 * Hook to get all companies (for admin users)
 */
export const useCompanies = () => {
  return useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      try {
        const response = await companiesAPI.getAll();
        console.log('Companies response:', response.data);
        
        let companies = [];
        if (response.data) {
          if (response.data.Success && Array.isArray(response.data.Data)) {
            companies = response.data.Data;
          } else if (response.data.success && Array.isArray(response.data.data)) {
            companies = response.data.data;
          } else if (Array.isArray(response.data)) {
            companies = response.data;
          }
        }
        
        return companies.map(company => ({
          id: company.Id || company.id,
          name: company.Name || company.name || company.companyName,
          email: company.Email || company.email,
          phone: company.Phone || company.phone,
          currency: company.Currency || company.currency || 'USD',
          address: company.Address || company.address,
          taxId: company.TaxId || company.taxId,
          fiscalYearEnd: company.FiscalYearEnd || company.fiscalYearEnd,
          status: company.Status || company.status,
          createdAt: company.CreatedAt || company.createdAt,
          updatedAt: company.UpdatedAt || company.updatedAt
        }));
      } catch (error) {
        console.error('Error fetching companies:', error);
        return [];
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to create a new company
 */
export const useCreateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (companyData) => {
      const response = await companiesAPI.create(companyData);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate companies list
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      
      // Add the new company to cache
      if (data.id || data.Id) {
        queryClient.setQueryData(['company', data.id || data.Id], data);
      }
    },
  });
};

/**
 * Hook to update company data
 */
export const useUpdateCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await companiesAPI.update(id, data);
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Update company in cache
      queryClient.setQueryData(['company', variables.id], data);
      
      // Invalidate companies list
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      
      // Update current user data if this is the current company
      const currentCompanyId = getCurrentCompanyId();
      if (variables.id === currentCompanyId) {
        setCurrentCompanyData(data);
      }
    },
  });
};

/**
 * Hook to delete a company
 */
export const useDeleteCompany = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      await companiesAPI.delete(id);
      return { success: true };
    },
    onSuccess: (data, id) => {
      // Remove company from cache
      queryClient.removeQueries({ queryKey: ['company', id] });
      
      // Invalidate companies list
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
};